import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { SharedUiThemeProvider, LoadingState, ErrorState } from '@harver/shared-ui';

import { defaultTheme } from '../branding/theme';
import { GlobalStyle } from '../components/GlobalStyle';
import { getBrandedConfig, OrganizationConfig } from '../branding';
import ContentContextProvider from '../branding/content.context';
import { Layout } from '../components/Layout';
import { Confirmation } from './confirmation';
import { Unavailable } from './unavailable';
import { Cancellation } from './cancellation';
import { Rescheduling } from './rescheduling';
import { Scheduler } from './scheduler';
import { Loader as TimePickerInitialLoader } from './scheduler/Loader';

function getFaviconElement() {
    return document.getElementById('favicon') as HTMLLinkElement;
}

enum Paths {
    timePicker = '/interview/timepicker',
    unavailable = '/interview/unavailable',
    confirmation = '/interview/confirmation',
    cancel = '/interview/cancel',
    reschedule = '/interview/reschedule',
}

function getLoader(pathname: string) {
    if (pathname === Paths.timePicker) {
        return <TimePickerInitialLoader />;
    }

    return <LoadingState testId="app.loading" />;
}

interface BrandedConfigState {
    isLoading: boolean;
    error?: any;
    config?: OrganizationConfig;
}

function App() {
    const params = new URLSearchParams(window.location.search);
    const calendarEventId = params.get('ref') as string;
    const [brandedConfig, setBrandedConfig] = useState<BrandedConfigState>({
        isLoading: true,
    });

    useEffect(() => {
        getBrandedConfig(calendarEventId)
            .then((config) => {
                const faviconEl = getFaviconElement();
                if (faviconEl) faviconEl.href = config.favicon;

                if (config.content.locale) {
                    // Add the locale to html lang attribute
                    document.documentElement.lang = config.content.locale.replace('_', '-');
                }

                setBrandedConfig({
                    isLoading: false,
                    config,
                });
            })
            .catch((error) => {
                setBrandedConfig({
                    isLoading: false,
                    error,
                });
            });
    }, [calendarEventId]);

    if (brandedConfig.isLoading) {
        return (
            <SharedUiThemeProvider theme={defaultTheme}>
                <GlobalStyle />
                <Layout>{getLoader(window.location.pathname)}</Layout>
            </SharedUiThemeProvider>
        );
    }

    if (brandedConfig.error || !brandedConfig.config) {
        document.title = 'Something went wrong';

        return (
            <SharedUiThemeProvider theme={defaultTheme}>
                <GlobalStyle />
                <ErrorState testId="branding.errorScreen" onReloadClick={() => window.location.reload()} />
            </SharedUiThemeProvider>
        );
    }

    return (
        <SharedUiThemeProvider theme={brandedConfig.config.theme}>
            <GlobalStyle />
            <Layout>
                <Router>
                    <ContentContextProvider value={brandedConfig.config.content}>
                        <Route path={Paths.unavailable} component={Unavailable} />
                        <Route path={Paths.confirmation} component={Confirmation} />
                        <Route path={Paths.cancel} component={Cancellation} />
                        <Route path={Paths.reschedule} component={Rescheduling} />
                        <Route path={Paths.timePicker} component={Scheduler} />
                    </ContentContextProvider>
                </Router>
            </Layout>
        </SharedUiThemeProvider>
    );
}

export default App;
